import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { concatMap } from 'rxjs/operators';

import { AvailabilityTimeCollectionModel } from '@core/models/agendamento-pratico/availability-time-collection.model';
import { CategoriaModel } from '@core/models/gestao/categoria.model';
import { CfcAgendamentoModel } from '@core/models/agendamento/cfc-agendamento.model';
import { CfcAgendamentoService } from '@core/services/apis/agendamento/cfc-agendamento.service';
import { CfcModel } from '@core/models/gestao/cfc.model';
import { CfcService } from '@core/services/apis/gestao/cfc.service';
import { ConstantModel } from '@core/models/outros/constant.model';
import { ContaCorrenteModel } from '@core/models/financeiro/conta-corrente.model';
import { CursoAgendamentoModel } from '@models/agendamento/curso-agendamento.model';
import { DelegaciaModel } from '@core/models/gestao/delegacia.model';
import { GrupoFinanceiroModel } from '@core/models/financeiro/grupo-financeiro.model';
import { GrupoLancamentoModel } from '@core/models/financeiro/grupo-lancamento.model';
import { GrupoModel } from '@core/models/gestao/grupo.model';
import { InstrutorAgendamentoModel } from '@core/models/agendamento/instrutor-agendamento.model';
import { InstrutorModel } from '@core/models/gestao/instrutor.model';
import { LocalModel } from '@core/models/gestao/local.model';
import { LocalFotobiometriaModel } from '@core/models/gestao/local-fotobiometria.model';
import { OrganizationModel } from '@core/models/agendamento-pratico/organization.model';
import { OrganizationService } from '@core/services/apis/agendamento-pratico/organization.service';
import { PessoaModel } from '@core/models/financeiro/pessoa.model';
import { ProvedorModel } from '@core/models/gestao/provedor.model';
import { ProdutoModel } from '@core/models/financeiro/produto.model';
import { RegiaoModel } from '@core/models/gestao/regiao.model';
import { ServicoAgendamentoModel } from '@core/models/agendamento/servico-agendamento.model';
import { ServicoGestaoModel } from '@core/models/gestao/servico-gestao.model';
import { TipoDeLancamentoModel } from '@core/models/financeiro/tipo-de-lancamento.model';
import { UnidadeDetranModel } from '@core/models/gestao/unidade-detran.model';
import { VeiculoCfcModel } from '@core/models/gestao/veiculo-cfc.model';
import { VeiculoModel } from '@core/models/gestao/veiculo.model';
import { VehicleService } from '@core/services/apis/agendamento-pratico/vehicle.service';
import { VeiculoService } from '@core/services/apis/gestao/veiculo.service';
import { InstructorOrganizationsModel } from '@core/models/agendamento-pratico/instructor-organization.model';
import { VehicleOrganizationModel } from '@core/models/agendamento-pratico/vehicle-organization.model';
import { ProgramContentModel } from '@core/models/agendamento/program-content.model';
import { BairroModel } from '../../models/gestao/bairro.model';
import { LocalAtendimentoDetranModel } from '@core/models/gestao/local-atendimento-detran-model';

@Injectable({
  providedIn: 'root'
})
export class ServicesSubject {

  //Gestão
  private _categoriaSubject$ = new BehaviorSubject<CategoriaModel[]>(null);
  private _cfcSubject$ = new BehaviorSubject<CfcModel[]>(null);
  private _delegaciaSubject$ = new BehaviorSubject<DelegaciaModel[]>(null);
  private _grupoSubject$ = new BehaviorSubject<GrupoModel[]>(null);
  private _instrutorSubject$ = new BehaviorSubject<InstrutorModel[]>(null);
  private _locaisFotobiometriaSubject$ = new BehaviorSubject<LocalFotobiometriaModel[]>(null);
  private _locaisSubject$ = new BehaviorSubject<LocalModel[]>(null);
  private _locaisDetranAtendimentoSubject$ = new BehaviorSubject<LocalAtendimentoDetranModel[]>(null); 
  private _regiaoSubject$ = new BehaviorSubject<RegiaoModel[]>(null);
  private _servicosSubject$ = new BehaviorSubject<ServicoGestaoModel[]>(null);
  private _provedoresSubject$ = new BehaviorSubject<ProvedorModel[]>(null);
  private _tipoExameSubject$ = new BehaviorSubject<ConstantModel[]>(null);
  private _unidadeDetranSubject$ = new BehaviorSubject<UnidadeDetranModel[]>(null);
  private _veiculoSubject$ = new BehaviorSubject<VeiculoModel[]>(null);
  private _veiculoCfcSubject$ = new BehaviorSubject<VeiculoCfcModel[]>(null);
  private _bairroSubject$ = new BehaviorSubject<BairroModel[]>(null);

  //Teórico
  private _cfcAgendamentoSubject$ = new BehaviorSubject<CfcAgendamentoModel[]>(null);
  private _cursoAgendamentoSubject$ = new BehaviorSubject<CursoAgendamentoModel[]>(null);
  private _conteudoProgramaticoSubject$ = new BehaviorSubject<ProgramContentModel[]>(null);
  private _instrutorAgendamentoSubject$ = new BehaviorSubject<InstrutorAgendamentoModel[]>(null);
  private _servicoAgendamentoSubject$ = new BehaviorSubject<ServicoAgendamentoModel[]>(null);

  //Prático
  private _availabilityTimeCollection$ = new BehaviorSubject<AvailabilityTimeCollectionModel[]>(null);
  private _instrutorPraticoSubject$ = new BehaviorSubject<InstructorOrganizationsModel[]>(null);
  private _organizationSubject$ = new BehaviorSubject<OrganizationModel[]>(null);
  private _veiculoPraticoSubject$ = new BehaviorSubject<VehicleOrganizationModel[]>(null);

  //Financeiro
  private _contaCorrente$ = new BehaviorSubject<ContaCorrenteModel[]>(null);
  private _grupoFinanceiroSubject$ = new BehaviorSubject<GrupoFinanceiroModel[]>(null);
  private _grupoLancamentoSubject$ = new BehaviorSubject<GrupoLancamentoModel[]>(null);
  private _pessoaSubject$ = new BehaviorSubject<PessoaModel[]>(null);
  private _produtosSubject$ = new BehaviorSubject<ProdutoModel[]>(null);
  private _tipoLancamentoSubject$ = new BehaviorSubject<TipoDeLancamentoModel[]>(null);

  constructor(
    private _veiculoPraticoService: VehicleService,
    private _veiculoGestaoService: VeiculoService,
    private _cfcService: CfcService,
    private _cfcAgendamentoService: CfcAgendamentoService,
    private _organizationService: OrganizationService,

  ) { }

      /******************************************* */
     /*                                           */
    /*                 GESTÃO                    */
   /*                                           */
  /*********************************************/

  //CATEGORIA
  getCategoria(): Observable<CategoriaModel[]> {
    return this._categoriaSubject$.asObservable();
  }

  setCategoria(tipo: CategoriaModel[]): void {
    this._categoriaSubject$.next(tipo);
  }

  //CFC
  getCfcsList(): CfcModel[] {
    return this._cfcSubject$.getValue();
  }

  getCfcs(): Observable<CfcModel[]> {
    return this._cfcSubject$.asObservable();
  }

  setCfc(cfcs: CfcModel[]): void {
    this._cfcSubject$.next(cfcs);
  }

  //DELEGACIA
  getDelegacia(): Observable<DelegaciaModel[]> {
    return this._delegaciaSubject$.asObservable();
  }

  setDelegacia(delegacia: DelegaciaModel[]): void {
    this._delegaciaSubject$.next(delegacia);
  }

  //GRUPO
  getGrupo(): Observable<GrupoModel[]> {
    return this._grupoSubject$.asObservable();
  }

  setGrupo(cfcs: GrupoModel[]): void {
    this._grupoSubject$.next(cfcs);
  }

  //INSTRUTOR
  getInstrutor(): Observable<InstrutorModel[]> {
    return this._instrutorSubject$.asObservable();
  }

  setInstrutor(instrutor: InstrutorModel[]): void {
    this._instrutorSubject$.next(instrutor);
  }

  //LOCAL FOTOBIOMETRIA
  setLocalFotoBiometria(locais: LocalFotobiometriaModel[]) {
    this._locaisFotobiometriaSubject$.next(locais);
  }

  getLocalFotoBiometria(): Observable<LocalFotobiometriaModel[]> {
    return this._locaisFotobiometriaSubject$.asObservable();
  }

  //LOCAL
  getLocal(): Observable<LocalModel[]> {
    return this._locaisSubject$.asObservable();
  }

  setLocal(locais: LocalModel[]) {
    this._locaisSubject$.next(locais);
  }

  //LOCAL ATENDIMENTO DETRAN
  getLocalAtendimentoDetran(): Observable<LocalAtendimentoDetranModel[]> {
    return this._locaisDetranAtendimentoSubject$.asObservable();
  }

  setLocalAtendimentoDetran(locaisAtendimentoDetran: LocalAtendimentoDetranModel[]) {
    this._locaisDetranAtendimentoSubject$.next(locaisAtendimentoDetran);
  }

  //REGIAO
  getProvedorList(): ProvedorModel[] {
    return this._provedoresSubject$.getValue();
  }

  getProvedor(): Observable<ProvedorModel[]> {
    return this._provedoresSubject$.asObservable();
  }

  setProvedor(provedor: ProvedorModel[]): void {
    this._provedoresSubject$.next(provedor);
  }

  //REGIAO
  getRegiao(): Observable<RegiaoModel[]> {
    return this._regiaoSubject$.asObservable();
  }

  setRegiao(regiao: RegiaoModel[]): void {
    this._regiaoSubject$.next(regiao);
  }

  //SERVICOS
  getServicos(): Observable<ServicoGestaoModel[]> {
    return this._servicosSubject$.asObservable();
  }

  setServicos(servicos: ServicoGestaoModel[]) {
    this._servicosSubject$.next(servicos);
  }

  //TIPO EXAME
  getTipoExame(): Observable<ConstantModel[]> {
    return this._tipoExameSubject$.asObservable();
  }

  setTipoExame(tipoExame: ConstantModel[]) {
    this._tipoExameSubject$.next(tipoExame);
  }

  //UNIDADE DETRAN
  getUnidadeDetran(): Observable<UnidadeDetranModel[]> {
    return this._unidadeDetranSubject$.asObservable();
  }

  setUnidadeDetran(unidadeDetran: UnidadeDetranModel[]): void {
    this._unidadeDetranSubject$.next(unidadeDetran);
  }

  //VEICULO
  getVeiculo(): Observable<VeiculoModel[]> {
    return this._veiculoSubject$.asObservable();
  }

  setVeiculo(veiculo: VeiculoModel[]): void {
    this._veiculoSubject$.next(veiculo);
  }

  //VEICULO CFC
  getVeiculoCfc(): Observable<VeiculoCfcModel[]> {
    return this._veiculoCfcSubject$.asObservable();
  }

  setVeiculoCfc(veiculo: VeiculoCfcModel[]): void {
    this._veiculoCfcSubject$.next(veiculo);
  }

  getBairro(): Observable<BairroModel[]> {
    return this._bairroSubject$.asObservable()
  }

  setBairro(bairro: BairroModel[]): void {
    this._bairroSubject$.next(bairro);
  }
      /******************************************* */
     /*                                           */
    /*                 TEÓRICO                   */
   /*                                           */
  /*********************************************/

  //CFC AGENDAMENTO
  getCfcsAgendamento(): Observable<CfcAgendamentoModel[]> {
    return this._cfcAgendamentoSubject$.asObservable();
  }

  getCfcsAgendamentoList(): CfcAgendamentoModel[] {
    return this._cfcAgendamentoSubject$.getValue();
  }

  setCfcAgendamento(cfcs: CfcAgendamentoModel[]): void {
    this._cfcAgendamentoSubject$.next(cfcs);
  }

  //CURSO AGENDAMENTO
  getCursoAgendamento(): Observable<CursoAgendamentoModel[]> {
    return this._cursoAgendamentoSubject$.asObservable();
  }

  setCursoAgendamento(cfcs: CursoAgendamentoModel[]): void {
    this._cursoAgendamentoSubject$.next(cfcs);
  }

  //CONTEUDO PROGRAMATICO
  getConteudoProgramatico(): Observable<ProgramContentModel[]> {
    return this._conteudoProgramaticoSubject$.asObservable();
  }

  setConteudoProgramatico(conteudo: ProgramContentModel[]): void {
    this._conteudoProgramaticoSubject$.next(conteudo);
  }

  //INSTRUTOR AGENDAMENTO
  getInstrutorAgendamento(): Observable<InstrutorAgendamentoModel[]> {
    return this._instrutorAgendamentoSubject$.asObservable();
  }

  getInstrutorAgendamentoList(): InstrutorAgendamentoModel[] {
    return this._instrutorAgendamentoSubject$.getValue();
  }

  setInstrutorAgendamento(instrutor: InstrutorAgendamentoModel[]): void {
    this._instrutorAgendamentoSubject$.next(instrutor);
  }

  //SERVICO AGENDAMENTO
  getServicoAgendamento(): Observable<ServicoAgendamentoModel[]> {
    return this._servicoAgendamentoSubject$.asObservable();
  }

  setServicoAgendamento(servicos: ServicoAgendamentoModel[]) {
    this._servicoAgendamentoSubject$.next(servicos);
  }

      /******************************************* */
     /*                                           */
    /*                 PRÁTICO                   */
   /*                                           */
  /*********************************************/

  //AVAILABILITY TIME COLLECTION
  getAvailabilityTimeCollection(): Observable<AvailabilityTimeCollectionModel[]> {
    return this._availabilityTimeCollection$.asObservable();
  }

  gettAvailabilityTimeCollectionList(): AvailabilityTimeCollectionModel[] {
    return this._availabilityTimeCollection$.getValue();
  }

  settAvailabilityTimeCollection(availabilityTimeCollection: AvailabilityTimeCollectionModel[]): void {
    this._availabilityTimeCollection$.next(availabilityTimeCollection);
  }

  //INSTRUTOR PRATICO
  getInstrutorPratico(): Observable<InstructorOrganizationsModel[]> {
    return this._instrutorPraticoSubject$.asObservable();
  }

  getInstrutorPraticoList(): InstructorOrganizationsModel[] {
    return this._instrutorPraticoSubject$.getValue();
  }

  setInstrutorPratico(instructor: InstructorOrganizationsModel[]): void {
    this._instrutorPraticoSubject$.next(instructor);
  }

  //ORGANIZATION (CFC PRÁTICO)
  getOrganization(): Observable<OrganizationModel[]> {
    return this._organizationSubject$.asObservable();
  }

  getOrganizationList(): OrganizationModel[] {
    return this._organizationSubject$.getValue();
  }

  setOrganization(cfcs: OrganizationModel[]): void {
    this._organizationSubject$.next(cfcs);
  }

  //VEÍCULO PRÁTICO
  getVeiculoPratico(): Observable<VehicleOrganizationModel[]> {
    return this._veiculoPraticoSubject$.asObservable();
  }

  getVeiculoPraticoList(): VehicleOrganizationModel[] {
    return this._veiculoPraticoSubject$.getValue();
  }

  setVeiculoPratico(vehicle: VehicleOrganizationModel[]): void {
    this._veiculoPraticoSubject$.next(vehicle);
  }

      /******************************************* */
     /*                                           */
    /*               FINANCEIRO                  */
   /*                                           */
  /*********************************************/

  //CONTA CORRENTE
  getContaCorrente(): Observable<ContaCorrenteModel[]> {
    return this._contaCorrente$.asObservable();
  }

  getContaCorrenteList(): ContaCorrenteModel[] {
    return this._contaCorrente$.getValue();
  }

  setContaCorrente(contas: ContaCorrenteModel[]): void {
    this._contaCorrente$.next(contas);
  }

  //GRUPO FINANCEIRO
  getGrupoFinanceiro(): Observable<GrupoFinanceiroModel[]> {
    return this._grupoFinanceiroSubject$.asObservable();
  }

  setGrupoFinanceiro(grupoFinanceiro: GrupoFinanceiroModel[]): void {
    this._grupoFinanceiroSubject$.next(grupoFinanceiro);
  }

  //GRUPO LANÇAMENTO
  getGrupoLancamento(): Observable<GrupoModel[]> {
    return this._grupoLancamentoSubject$.asObservable();
  }

  setGrupoLancamento(grupoLancamentoModel: GrupoLancamentoModel[]): void {
    this._grupoLancamentoSubject$.next(grupoLancamentoModel);
  }

  //PESSOA
  getPessoa(): Observable<PessoaModel[]> {
    return this._pessoaSubject$.asObservable();
  }

  setPessoa(pessoa: PessoaModel[]): void {
    this._pessoaSubject$.next(pessoa);
  }

  //PRODUTOS
  getProdutos(): Observable<ProdutoModel[]> {
    return this._produtosSubject$.asObservable();
  }

  setProdutos(produto: ProdutoModel[]): void {
    this._produtosSubject$.next(produto);
  }

  //TIPO LANÇAMENTO
  getTipoLancamento(): Observable<TipoDeLancamentoModel[]> {
    return this._tipoLancamentoSubject$.asObservable();
  }

  setTipoLancamento(tipo: TipoDeLancamentoModel[]): void {
    this._tipoLancamentoSubject$.next(tipo);
  }

  reloadVehicles(): void {
    this._veiculoGestaoService.getAll().pipe(
      concatMap(result => {
        this.setVeiculo(result);
        return this._veiculoPraticoService.getAll()
      })
    ).subscribe((res) => {
      this.setVeiculoPratico(res);
    })
  }

  reloadCfcs(): void {
    this._cfcService.getAll().pipe(
      concatMap(result => {
        this.setCfc(result);
        return this._cfcAgendamentoService.getAll()
      }),
      concatMap(result => {
        this.setCfcAgendamento(result);
        return this._organizationService.getAll()
      })
    ).subscribe((res) => {
      this.setOrganization(res);
    })
  }
}
